/**
 * ProgressBar Component
 *
 * A flexible progress bar with label and value display.
 *
 * @example
 * ```jsx
 * // Basic usage with percentage
 * <ProgressBar
 *   label="Contract time"
 *   value={85}
 *   valueLabel="301 days remaining"
 * />
 *
 * // With current/max values
 * <ProgressBar
 *   label="Hours used"
 *   current={14}
 *   max={40}
 *   valueLabel="14 / 40 hrs"
 *   color="warning"
 * />
 *
 * // Auto-generate value label
 * <ProgressBar
 *   label="Storage"
 *   current={750}
 *   max={1000}
 *   unit="MB"
 * />
 *
 * // Different colors
 * <ProgressBar label="Health" value={90} color="success" />
 * <ProgressBar label="Risk" value={75} color="warning" />
 * <ProgressBar label="Critical" value={95} color="error" />
 *
 * // Inverse - bar fills up instead of emptying
 * <ProgressBar
 *   label="Contract progress"
 *   current={270}
 *   max={365}
 *   valueLabel="270 days elapsed"
 *   inverse
 * />
 * ```
 */

const classNames = (...classes) => classes.filter(Boolean).join(' ');

const COLOR_CLASSES = {
	success: 'bg-emerald-500',
	warning: 'bg-amber-500',
	error: 'bg-red-500',
	info: 'bg-blue-500',
	purple: 'bg-purple-500',
	indigo: 'bg-indigo-500',
	default: 'bg-gray-500',
};

const SIZE_CONFIG = {
	xs: { bar: 'h-1', text: 'text-xs', gap: 'mb-1' },
	sm: { bar: 'h-1.5', text: 'text-xs', gap: 'mb-1.5' },
	md: { bar: 'h-2', text: 'text-sm', gap: 'mb-2' },
	lg: { bar: 'h-3', text: 'text-base', gap: 'mb-2' },
};

const ProgressBar = ({
	// Content
	label,
	valueLabel,
	value, // 0-100 percentage
	current, // Current value (alternative to value)
	max = 100, // Max value when using current
	unit = '', // Unit suffix for auto-generated label

	// Styling
	color = 'success',
	size = 'sm',
	rounded = true,
	showLabel = true,
	showValue = true,
	animate = false,
	inverse = false, // When true, bar fills up (shows elapsed) instead of emptying (shows remaining)

	// Custom classes
	className = '',
	barClassName = '',
	labelClassName = '',
	valueClassName = '',
}) => {
	// Calculate percentage
	const rawPercentage = value !== undefined ? value : (current / max) * 100;
	const percentage = inverse ? 100 - rawPercentage : rawPercentage;
	const clampedPercentage = Math.min(100, Math.max(0, percentage));

	// Auto-generate value label if not provided
	const displayValueLabel = valueLabel || (
		current !== undefined
			? `${current} / ${max}${unit ? ` ${unit}` : ''}`
			: `${Math.round(clampedPercentage)}%`
	);

	// Get color - can be a preset or a custom Tailwind class
	const barColor = COLOR_CLASSES[color] || color;

	// Get size config
	const sizeConfig = SIZE_CONFIG[size] || SIZE_CONFIG.sm;

	return (
		<div className={className}>
			{/* Label row */}
			{(showLabel || showValue) && (
				<div className={classNames('flex items-center justify-between', sizeConfig.gap)}>
					{showLabel && label && (
						<span
							className={classNames(
								'text-gray-600 font-medium',
								sizeConfig.text,
								labelClassName,
							)}
						>
							{label}
						</span>
					)}
					{showValue && (
						<span
							className={classNames(
								'text-gray-500',
								sizeConfig.text,
								valueClassName,
							)}
						>
							{displayValueLabel}
						</span>
					)}
				</div>
			)}

			{/* Progress bar */}
			<div
				className={classNames(
					'w-full bg-gray-100 overflow-hidden',
					sizeConfig.bar,
					rounded ? 'rounded-full' : '',
				)}
			>
				<div
					className={classNames(
						'h-full transition-all duration-300 ease-out',
						barColor,
						rounded ? 'rounded-full' : '',
						animate ? 'animate-pulse' : '',
						barClassName,
					)}
					style={{ width: `${clampedPercentage}%` }}
				/>
			</div>
		</div>
	);
};

/**
 * ProgressBarGroup - Display multiple progress bars in a stack
 */
export const ProgressBarGroup = ({ children, gap = 'md', className = '' }) => {
	const gapClasses = {
		sm: 'space-y-3',
		md: 'space-y-4',
		lg: 'space-y-6',
	};

	return (
		<div className={classNames(gapClasses[gap] || gapClasses.md, className)}>
			{children}
		</div>
	);
};

export default ProgressBar;
